import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Image,
    Link,
    Text,
    makeStyles,
    mergeClasses,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';

import page1Image from '../../../assets/quick-guide/GuidePage1.png';
import customGPTs from '../../../assets/quick-guide/CustomGPTs.png';
import PageOneBar from '../../../assets/quick-guide/page1bar.svg';
import PageTwoBar from '../../../assets/quick-guide/page2bar.svg';
import PageThreeBar from '../../../assets/quick-guide/page3bar.svg';
import PageFourBar from '../../../assets/quick-guide/page4bar.svg';
import CloseIcon from '../../../assets/quick-guide/Close.png';
import { AlwaysOnScrollBarStyles, Breakpoints, customColors, customFonts } from '../../../styles';
import { setShowQuickGuide } from '../../../redux/features/commonData/commonDataSlice';

const useStyles = makeStyles({
    dialog: {
        minWidth: '900px',
        maxWidth: '1000px',
        width: '60%',
        height: '70%',
        minHeight: '600px',
        ...Breakpoints.small({
            minWidth: '680px',
            width: '70%',
            maxWidth: '850px',
            height: '60%',
            minHeight: '500px',
        }),
        '@media (max-width: 768px)': {
            minWidth: '350px',
            width: 'clamp(350px, 80%, 690px)',
            overflowY: 'scroll',
            ...AlwaysOnScrollBarStyles,
        },
    },
    dialogTitle: {
        marginTop: '10px',
        marginBottom: '10px',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    titleContainer: {
        width: 'min-content',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        gap: tokens.spacingVerticalM,
        marginBottom: tokens.spacingVerticalM,
    },
    title: {
        textAlign: 'center',
        color: customColors.protivitiDarkBlue,
        font: customFonts.TitleFont,
        fontWeight: '700',
        fontSize: '24px',
    },
    pageProgressBar: {
        width: 'clamp(300px, 40%, 330px)',
        height: 'clamp(30px, 100%, 30px)',
        '@media (max-width: 768px)': {
            width: 'clamp(250px, 40%, 330px)',
            height: 'clamp(30px, 100%, 30px)',
        },
    },
    closeButton: {
        marginBottom: '50px',
        ...Breakpoints.small({
            maxWidth: '24px',
            maxHeight: '24px',
        }),
    },
    dialogBody: {
        height: '90%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: '10px',
        '@media (max-width: 768px)': {
            height: '80%',
        },
    },
    dialogContent: {
        height: '100%',
        width: '90%',
        paddingBottom: '20px',
        overflowY: 'auto',
        ...AlwaysOnScrollBarStyles,
        ...Breakpoints.small({
            width: '100%',
        }),
    },
    pageRoot: {
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '20px',
        ...Breakpoints.small({
            height: '90%',
            gap: '10px',
        }),
        '@media (max-width: 768px)': {
            gap: '20px',
            height: 'fit-content',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
        },
    },
    imageContainer: {
        width: 'clamp(350px,40%,390px)',
        borderRadius: '16px',
        ...Breakpoints.small({
            minWidth: '250px',
            width: '300px',
            height: '100%',
        }),
    },
    image: {
        borderRadius: '16px',
    },
    textContainer: {
        flex: 1,
        width: 'clamp(250px, 60%, 575px)',
        height: '95%',
        paddingLeft: '10px',
        paddingRight: '5%',
        paddingTop: '10px',
        paddingBottom: '10px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        ...Breakpoints.small({
            justifyContent: 'flex-start',
            paddingLeft: '0',
            height: '100%',
            paddingTop: '0',
        }),
        '@media (max-width: 768px)': {
            width: '80%',
            maxWidth: '80%',
            paddingLeft: '10px',
            paddingRight: '10px',
        },
    },
    pageTitle: {
        width: '100%',
        textAlign: 'left',
        font: customFonts.Lato,
        fontWeight: '700',
        fontSize: 'clamp(36px,1.5em,50px)',
        color: 'rgba(0, 66, 107, 0.75)',
        lineHeight: '36px',
        ...Breakpoints.small({
            fontSize: 'clamp(1.2rem, 24px, 2rem)',
            lineHeight: 'clamp(1.2rem, 2.5vw, 2rem)',
        }),
    },
    description: {
        font: customFonts.Lato,
        fontWeight: '400',
        fontSize: '18px',
        color: '#46596b',
        lineHeight: 'clamp(18px, 3vh, 32px)',
        ...Breakpoints.small({
            fontSize: '16px',
            lineHeight: '20px',
        }),
    },
    centeredTextContainer: {
        flex: 1,
        width: 'clamp(250px, 50%, 575px)',
        height: '95%',
        paddingLeft: '10px',
        paddingRight: '5%',
        paddingTop: '10px',
        paddingBottom: '10px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        ...Breakpoints.small({
            justifyContent: 'flex-start',
            paddingLeft: '0',
            height: '100%',
            paddingTop: '0',
        }),
        '@media (max-width: 768px)': {
            width: '80%',
            maxWidth: '80%',
            paddingLeft: '10px',
            paddingRight: '10px',
        },
    },
    centeredTitle: {
        width: '100%',
        textAlign: 'left',
        font: customFonts.Lato,
        fontWeight: '700',
        fontSize: '36px',
        color: 'rgba(0, 66, 107, 0.75)',
        marginBottom: '15px',
    },
    inlineLinkButton: {
        textAlign: 'left',
        font: customFonts.Lato,
        fontWeight: '400',
        fontSize: '18px',
        color: customColors.protivitiOrange,
        lineHeight: '32px',
        textDecoration: 'underline',
        alignSelf: 'start',
        paddingLeft: '0px',
    },
    videoPage: {
        height: '90%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    videoTitle: {
        width: '100%',
        textAlign: 'center',
        font: customFonts.Lato,
        fontWeight: '700',
        fontSize: '36px',
        color: 'rgba(0, 66, 107, 0.75)',
        marginBottom: '20px',
        '@media (max-width: 768px)': {
            fontSize: '24px',
        },
    },
    videoContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    video: {
        width: '70%',
        height: '100%',
    },
    resourcesPage: {
        height: '80%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-around',
    },
    sectionTitle: {
        width: '100%',
        textAlign: 'center',
        font: customFonts.Lato,
        fontWeight: '700',
        fontSize: '36px',
        color: 'rgba(0, 66, 107, 0.75)',
        marginBottom: '20px',
        '@media (max-width: 768px)': {
            fontSize: '24px',
            marginBottom: '10px',
        },
    },
    sectionText: {
        font: customFonts.Lato,
        fontWeight: '400',
        fontSize: '18px',
        color: '#46596b',
        lineHeight: '32px',
        '@media (max-width: 768px)': {
            fontSize: '16px',
            marginBottom: '10px',
        },
    },
    resourcesContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    resourceLink: {
        textAlign: 'center',
        font: customFonts.Lato,
        fontWeight: '400',
        fontSize: '18px',
        color: customColors.protivitiOrange,
        lineHeight: '32px',
        textDecoration: 'underline',
        paddingLeft: '0px',
        '@media (max-width: 768px)': {
            fontSize: '16px',
            marginBottom: '10px',
            lineHeight: '24px',
        },
    },
    dialogActions: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '15px',
        '@media (max-width: 768px)': {
            marginTop: '10px',
        },
    },
    navigationButton: {
        borderRadius: '18px',
        width: '150px',
        maxHeight: '32px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    secondaryButton: {
        ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),
        color: customColors.protivitiDarkBlue,
    },
    primaryButton: {
        ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
        backgroundColor: customColors.protivitiOrange,
        color: 'white',
    },
});

interface GettingStartedLinks {
    walkthroughVideo?: string;
    FAQs?: string;
    ProGPTvsAtlas?: string;
    GenAI?: string;
}

export const GettingStartedDialog: React.FC = () => {
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { showQuickGuide } = useAppSelector((state) => state.commonData);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [links, setLinks] = useState<GettingStartedLinks>({});

    useEffect(() => {
        if (!process.env.REACT_APP_GETTING_STARTED) {
            return;
        }

        try {
            setLinks(JSON.parse(process.env.REACT_APP_GETTING_STARTED) as GettingStartedLinks);
        } catch (error) {
            console.error('Failed to parse getting started config:', error);
        }
    }, []);

    const closeDialog = () => dispatch(setShowQuickGuide(false));

    const openExploreAgents = () => {
        closeDialog();
        navigate('/explore-agents');
    };

    const slides = useMemo(
        () => [
            {
                progressImage: PageOneBar,
                leftButtonText: 'Show Me Later',
                rightButtonText: 'Next',
                content: (
                    <div className={classes.pageRoot}>
                        <div className={classes.imageContainer}>
                            <Image className={classes.image} src={page1Image} fit="contain" />
                        </div>
                        <div className={classes.textContainer}>
                            <Text className={classes.pageTitle}>What is ProGPT?</Text>
                            <Text className={classes.description}>
                                ProGPT is an OpenAI-powered application, similar to ChatGPT, designed exclusively for
                                Protiviti employees. Housed securely behind our firewall, ProGPT harnesses the
                                capabilities of AI without the concern of sensitive data exposure to the public. Just
                                like other generative AI chatbots, you can enter a prompt using conversational language
                                and ProGPT will provide iterative responses.
                            </Text>
                        </div>
                    </div>
                ),
            },
            {
                progressImage: PageTwoBar,
                leftButtonText: 'Back',
                rightButtonText: 'Next',
                content: (
                    <div className={classes.pageRoot}>
                        <div className={classes.centeredTextContainer}>
                            <Text className={classes.centeredTitle}>Specialized Agents</Text>
                            <Text className={classes.description}>
                                Specialized Agents are assistants tailored to help with specific tasks and are augmented
                                by specific curated knowledge sources.
                            </Text>
                            <Button
                                className={classes.inlineLinkButton}
                                appearance="transparent"
                                onClick={openExploreAgents}
                            >
                                Explore specialized Agents
                            </Button>
                        </div>
                        <div className={classes.imageContainer}>
                            <Image src={customGPTs} fit="contain" />
                        </div>
                    </div>
                ),
            },
            {
                progressImage: PageThreeBar,
                leftButtonText: 'Back',
                rightButtonText: 'Next',
                content: (
                    <div className={classes.videoPage}>
                        <div className={classes.videoTitle}>Walkthrough</div>
                        <div className={classes.videoContainer}>
                            <iframe
                                className={classes.video}
                                src={links.walkthroughVideo}
                                allowFullScreen
                                title="Navigating ProGPT"
                            />
                        </div>
                    </div>
                ),
            },
            {
                progressImage: PageFourBar,
                leftButtonText: 'Back',
                rightButtonText: 'Try ProGPT Now',
                content: (
                    <div className={classes.resourcesPage}>
                        <div>
                            <div className={classes.sectionTitle}>Questions?</div>
                            <div className={classes.sectionText}>
                                Contact{' '}
                                <Link
                                    className={classes.resourceLink}
                                    href="mailto:AI@protiviti.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    AI@protiviti.com
                                </Link>
                            </div>
                        </div>
                        <div className={classes.resourcesContainer}>
                            <div className={classes.sectionTitle}>Resources</div>
                            <Link
                                className={classes.resourceLink}
                                href={links.FAQs}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                ProGPT FAQs, Recent Feature Releases, and More
                            </Link>
                            <Link
                                className={classes.resourceLink}
                                href={links.ProGPTvsAtlas}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                ProGPT vs Atlas
                            </Link>
                            <Link
                                className={classes.resourceLink}
                                href={links.GenAI}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Learn More About Gen AI at Protiviti
                            </Link>
                        </div>
                    </div>
                ),
            },
        ],
        [classes, links, openExploreAgents],
    );

    const currentSlideData = slides[currentSlide];

    const handleLeftClick = () => {
        if (currentSlide === 0) {
            closeDialog();
            return;
        }

        setCurrentSlide((previous) => previous - 1);
    };

    const handleRightClick = () => {
        if (currentSlide === slides.length - 1) {
            closeDialog();
            return;
        }

        setCurrentSlide((previous) => previous + 1);
    };

    return (
        <Dialog open={showQuickGuide}>
            <DialogSurface className={classes.dialog}>
                <DialogBody className={classes.dialogBody}>
                    <DialogTitle className={classes.dialogTitle}>
                        <div className={classes.titleContainer}>
                            <div className={classes.title}>Getting Started With ProGPT</div>
                            <div className={classes.pageProgressBar}>
                                <Image src={currentSlideData.progressImage} fit="contain" />
                            </div>
                        </div>
                        <Button
                            className={classes.closeButton}
                            icon={<Image src={CloseIcon} fit="contain" />}
                            appearance="transparent"
                            onClick={closeDialog}
                        />
                    </DialogTitle>
                    <DialogContent className={classes.dialogContent}>{currentSlideData.content}</DialogContent>
                </DialogBody>
                <DialogActions className={classes.dialogActions}>
                    <Button
                        className={mergeClasses(classes.navigationButton, classes.secondaryButton)}
                        appearance="secondary"
                        onClick={handleLeftClick}
                    >
                        {currentSlideData.leftButtonText}
                    </Button>
                    <Button
                        className={mergeClasses(classes.navigationButton, classes.primaryButton)}
                        appearance="secondary"
                        onClick={handleRightClick}
                    >
                        {currentSlideData.rightButtonText}
                    </Button>
                </DialogActions>
            </DialogSurface>
        </Dialog>
    );
};

export default GettingStartedDialog;
